<template>
  <div>
    <validation-observer ref="validation">
      <v-btn-toggle
        v-model="eventTypeSelector"
        color="primary"
        mandatory
        class="v-btn-toggle--block mb-5"
      >
        <v-btn
          :value="eventTypeTraining"
          :disabled="editingRules && (!editingRules.canEditType || !editingRules.canEditToTraining)"
          @click="eventForm.event_type = eventTypeTraining"
        >
          {{ $t('event_type.training') }}
        </v-btn>
        <v-btn
          :value="eventTypeMatch"
          :disabled="editingRules && (!editingRules.canEditType || !editingRules.canEditToMatch)"
          @click="eventForm.event_type = eventTypeMatch"
        >
          {{ $t('event_type.match') }}
        </v-btn>
        <v-btn
          value="other"
          :disabled="editingRules && (!editingRules.canEditType || !editingRules.canEditToOther)"
          @click="eventForm.event_type = null"
        >
          {{ $t('event_type.other') }}
        </v-btn>
      </v-btn-toggle>

      <validation-provider
        v-if="eventTypeSelector === 'other'"
        v-slot="{ errors }"
        :rules="{ 'required': startsProgram }"
      >
        <v-select
          v-model="eventForm.event_type"
          outlined
          :label="$t('dropdown.event_type.title')"
          :items="otherEventTypes"
          item-value="key"
          item-text="text"
          item-disabled="disabled"
          clearable
          :disabled="editingRules && !editingRules.canEditType"
          :error-messages="errors"
        />
      </validation-provider>

      <validation-provider
        v-slot="{ errors }"
        :rules="{ 'required': true }"
      >
        <v-text-field
          v-model="eventForm.title"
          :error-messages="errors"
          :label="$t('text_input.title.title')"
          :placeholder="$t('text_input.title.placeholder')"
          outlined
        />
      </validation-provider>

      <v-row
        no-gutters
        class="mb-3"
      >
        <v-col>
          <subtitle>
            <v-icon
              left
              small
            >
              fal fa-clock
            </v-icon>
            {{ $t('picker.time.title') }}
          </subtitle>
        </v-col>
        <v-col
          cols="auto"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                class="text-none"
                text
                small
                v-on="on"
                @click="$emit('apply-all', 'time', { start_time: eventForm.start_time, end_time: eventForm.end_time})"
              >
                {{ $t('training_programs.dialog.event_form.apply_all') }}
              </v-btn>
            </template>
            <span>
              {{ $t('training_programs.dialog.event_form.applies_start_end_time') }}
            </span>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="mb-4"
      >
        <v-col
          cols="auto"
          class="pt-1"
        >
          <v-btn
            text
            icon
            color="grey"
            :disabled="editingRules && !editingRules.canEditTime"
            @click="eventForm.date = $dayjs(eventForm.date).subtract(1, 'day').format('YYYY-MM-DD'); updateEventTime()"
          >
            <v-icon small>
              far fa-minus
            </v-icon>
          </v-btn>
        </v-col>
        <v-col style="min-width: 110px">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-menu>
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="eventForm.date ? $dayjs(eventForm.date).format('L') : ''"
                  :error-messages="errors"
                  :label="$t('picker.date.title')"
                  outlined
                  dense
                  readonly
                  hide-details
                  :disabled="editingRules && !editingRules.canEditTime"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="eventForm.date"
                :first-day-of-week="1"
                no-title
                @change="updateEventTime()"
              />
            </v-menu>
          </validation-provider>
          <div class="caption grey--text mt-1">
            {{ $t('training_programs.dialog.event_form.day') }} <strong>{{ $dayjs(eventForm.date).diff($dayjs(startDate), 'day') + 1 }}</strong> {{ $t('training_programs.dialog.event_form.in_program') }}
          </div>
        </v-col>
        <v-col
          cols="auto"
          class="pt-1"
        >
          <v-btn
            text
            icon
            color="grey"
            :disabled="editingRules && !editingRules.canEditTime"
            @click="eventForm.date = $dayjs(eventForm.date).add(1, 'day').format('YYYY-MM-DD'); updateEventTime()"
          >
            <v-icon small>
              far fa-plus
            </v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-row no-gutters>
            <v-col
              class="pl-2"
              cols="auto"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required|time"
              >
                <v-combobox
                  v-model="eventForm.start_time"
                  :error-messages="errors"
                  dense
                  outlined
                  hide-details
                  :items="timeItems"
                  :disabled="editingRules && !editingRules.canEditTime"
                  style="max-width: 105px"
                  @blur="updateEventTime()"
                />
              </validation-provider>
            </v-col>
            <v-col
              cols="auto"
              class="pl-1 pt-3"
            >
              -
            </v-col>
            <v-col
              class="pl-1"
              cols="auto"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required|time"
              >
                <v-combobox
                  v-model="eventForm.end_time"
                  :error-messages="errors"
                  dense
                  outlined
                  hide-details
                  :items="timeItems"
                  :disabled="editingRules && !editingRules.canEditTime"
                  style="max-width: 105px"
                  @blur="updateEventTime()"
                />
              </validation-provider>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <validation-provider
        v-if="hasTimezoneManagement && eventForm.tz"
        v-slot="{ errors }"
        rules="required"
      >
        <v-autocomplete
          v-model="eventForm.tz"
          outlined
          :items="timezones"
          item-value="tzCode"
          item-text="name"
          dense
          :placeholder="$t('dropdown.current_timezone.placeholder')"
          :disabled="editingRules && !editingRules.canEditTime"
          :error-messages="errors"
        />
      </validation-provider>

      <div v-if="eventForm.event_type === eventTypeTestSession || eventForm.event_type === eventTypeReadiness">
        <subtitle class="mb-3">
          <v-icon
            left
            small
          >
            fal fa-list-check
          </v-icon>
          {{ $t('add_new.diagnostic_protocol') }}
        </subtitle>

        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-autocomplete
            v-model="eventForm.diagnostic_protocol_id"
            outlined
            :items="protocol.items"
            item-value="id"
            item-text="name"
            dense
            :error-messages="errors"
            :placeholder="$t('card_title.select_testing_protocol')"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </validation-provider>
      </div>

      <v-row
        v-if="hasOpenEventApplications"
        no-gutters
        align="center"
        :class="{ 'mb-6': !eventForm.is_open_application, 'mb-1': eventForm.is_open_application }"
      >
        <v-col cols="auto">
          <subtitle>
            <v-icon
              left
              small
            >
              fal fa-user-plus
            </v-icon>
            {{ $t('eventDetails.reservations.open_application') }}
          </subtitle>
        </v-col>
        <v-col
          cols="auto"
          class="pl-4"
        >
          <v-switch
            v-model="eventForm.is_open_application"
            :disabled="editingRules && !editingRules.canEditTime"
            class="pa-0 ma-0"
            hide-details
          />
        </v-col>
      </v-row>

      <div
        v-if="hasOpenEventApplications && eventForm.is_open_application"
        class="mb-8"
      >
        <div class="grey--text text-caption mb-3">
          {{ $t('eventDetails.reservations.allow_players_label') }}
        </div>

        <v-row no-gutters>
          <v-col
            cols="3"
            class="text-body-2 grey--text text--darken-2 pt-3"
          >
            {{ $t('eventDetails.reservations.max_participants') }}
          </v-col>
          <v-col
            cols="3"
          >
            <validation-provider
              v-slot="{ errors }"
              :rules="{ 'required': startsProgram, 'numeric': true }"
            >
              <v-text-field
                v-model="eventForm.max_participants"
                dense
                type="number"
                min="1"
                max="1000"
                :placeholder="$t('label.e_g') + ' ' + 100"
                hide-details
                outlined
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </v-row>

        <v-row
          class="mt-4"
          no-gutters
          align="center"
        >
          <v-col
            cols="3"
            class="text-body-2 grey--text text--darken-2"
          >
            {{ $t('eventDetails.reservations.applications_open') }}
          </v-col>
          <v-col cols="2">
            <validation-provider
              v-slot="{ errors }"
              rules="numeric"
            >
              <v-text-field
                v-model="eventForm.open_application_start_days_before"
                dense
                type="number"
                min="1"
                hide-details
                outlined
                suffix="d"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="2"
            class="pl-1"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="numeric"
            >
              <v-text-field
                v-model="eventForm.open_application_start_hours_before"
                dense
                type="number"
                hide-details
                outlined
                suffix="h"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="auto"
            class="text-body-2 grey--text text--darken-2 pl-3"
          >
            {{ $t('eventDetails.reservations.before') }}
          </v-col>
          <v-col
            v-if="openApplicationStartAt"
            class="grey--text caption pl-3 text-right"
          >
            {{ $dayjs(openApplicationStartAt).format('ddd, MMM D, HH:mm') }}
          </v-col>
        </v-row>

        <v-row
          class="mt-4"
          no-gutters
          align="center"
        >
          <v-col
            cols="3"
            class="text-body-2 grey--text text--darken-2"
          >
            {{ $t("eventDetails.reservations.applications_close") }}
          </v-col>
          <v-col cols="2">
            <validation-provider
              v-slot="{ errors }"
              rules="numeric"
            >
              <v-text-field
                v-model="eventForm.open_application_end_days_before"
                dense
                type="number"
                min="1"
                hide-details
                outlined
                suffix="d"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="2"
            class="pl-1"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="numeric"
            >
              <v-text-field
                v-model="eventForm.open_application_end_hours_before"
                dense
                type="number"
                hide-details
                outlined
                suffix="h"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="auto"
            class="text-body-2 grey--text text--darken-2 pl-3"
          >
            {{ $t('eventDetails.reservations.before') }}
          </v-col>
          <v-col
            v-if="openApplicationEndAt"
            class="grey--text caption pl-3 text-right"
          >
            {{ $dayjs(openApplicationEndAt).format('ddd, MMM D, HH:mm') }}
          </v-col>
        </v-row>

        <v-row
          class="mt-4"
          no-gutters
          align="center"
        >
          <v-col
            cols="6"
            class="text-body-2 grey--text text--darken-2"
          >
            {{ $t("eventDetails.reservations.limit_applications") }}
          </v-col>
          <v-col>
            <v-switch
              v-model="eventForm.is_open_application_team_constrained"
              class="pa-0 ma-0"
              hide-details
              dense
            />
          </v-col>
        </v-row>
      </div>

      <div v-if="startsProgram">
        <v-row
          no-gutters
          class="mb-3"
        >
          <v-col>
            <subtitle>
              <v-icon
                left
                small
              >
                fal fa-user-group
              </v-icon>
              {{ $t('add_new.team') }}
            </subtitle>
          </v-col>
          <v-col cols="auto">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  class="text-none"
                  text
                  small
                  v-on="on"
                  @click="$emit('apply-all', 'team_ids', eventForm.team_ids)"
                >
                  {{ $t('training_programs.dialog.event_form.apply_all') }}
                </v-btn>
              </template>
              <span>
                {{ $t('training_programs.dialog.event_form.applies_team') }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>

        <validation-provider
          v-slot="{ errors }"
          :rules="{ required: !!eventForm.is_open_application_team_constrained && !startsProgram }"
        >
          <v-autocomplete
            v-model="eventForm.team_ids"
            outlined
            :items="teamSelectItems"
            item-value="id"
            item-text="name"
            dense
            multiple
            :disabled="editingRules && !editingRules.canEditTeamsAndPlayers"
            :error-messages="errors"
            :placeholder="$t('select_team.placeholder')"
          />
        </validation-provider>

        <v-row
          no-gutters
          class="mb-3"
          align="center"
        >
          <v-col>
            <subtitle>
              <v-icon
                left
                small
              >
                fal fa-user
              </v-icon>
              {{ $t("dropdown.players.title") }}
            </subtitle>
          </v-col>
          <v-col
            class="grey--text text-caption"
            cols="auto"
          >
            {{ $t('added_players', {count: eventForm.player_ids.length}) }}
          </v-col>
          <v-col
            cols="auto"
            class="pl-3"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  class="text-none"
                  text
                  small
                  v-on="on"
                  @click="$emit('apply-all', 'player_ids', eventForm.player_ids)"
                >
                  {{ $t('training_programs.dialog.event_form.apply_all') }}
                </v-btn>
              </template>
              <span>
                {{ $t('training_programs.dialog.event_form.applies_players') }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>

        <validation-provider
          v-slot="{ errors }"
          :rules="{ required: false, max_selections: editingRules && editingRules.onlyOnePlayer ? { max: 1 } : false }"
        >
          <v-autocomplete
            v-model="eventForm.player_ids"
            outlined
            :items="playersToSelect"
            item-value="id"
            item-text="name"
            dense
            multiple
            :chips="false"
            :error-messages="errors"
            clearable
            :disabled="editingRules && !editingRules.canEditTeamsAndPlayers"
            :placeholder="$t('dropdown.players.placeholder')"
          >
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 3"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 3">{{ item.first_name }} {{ item.last_name }}</span>
              <span
                v-else-if="index === 3"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
            <template v-slot:item="{ item, on }">
              <v-list-item-action>
                <v-simple-checkbox
                  :value="eventForm.player_ids.indexOf(item.id) > -1"
                  :ripple="false"
                  v-on="on"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.last_name }}, {{ item.first_name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span>{{ item.team.short_name }}</span>
                  <span
                    v-if="item.player_positions && item.player_positions.length > 0"
                  >&middot; {{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </validation-provider>
      </div>

      <div v-if="hasTrainingBuilder">
        <div
          v-if="eventForm.event_type === eventTypeTraining || eventForm.event_type === eventTypeMatch || eventForm.event_type === eventTypeReadiness || eventForm.event_type === eventTypeRehab || eventForm.event_type === eventTypeIndividualWork"
          class="mb-6"
        >
          <v-row
            class="mb-0"
            align="center"
          >
            <v-col>
              <subtitle>
                <v-icon
                  left
                  small
                >
                  fal fa-list-check
                </v-icon>
                {{ $t('add_new.training') }}
              </subtitle>
            </v-col>
            <v-col
              cols="auto"
              class="pl-4"
            >
              <v-btn
                text
                small
                color="primary"
                class="text-none"
                @click="trainingProtocolDialog = true"
              >
                <v-icon
                  left
                  x-small
                >
                  far fa-pen
                </v-icon>
                {{ trainingProtocolItems.length === 0 ? $t('button.create_protocol') : $t('button.adjust_protocol') }}
              </v-btn>
            </v-col>
          </v-row>

          <validation-provider
            v-slot="{ errors }"
            :rules="{ required: false }"
          >
            <v-autocomplete
              v-model="trainingProtocol.selected"
              outlined
              :items="trainingProtocol.items"
              :loading="trainingProtocol.itemsLoading"
              item-value="id"
              item-text="name"
              dense
              :error-messages="errors"
              clearable
              return-object
              hide-details
              :menu-props="{ maxWidth: '540px' }"
              :placeholder="$t('dropdown.training_protocol.placeholder')"
              @change="changeTrainingProtocol"
              @click:clear="changeTrainingProtocol(null)"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </validation-provider>

          <div
            v-if="trainingProtocolItems.length > 0"
            class="mt-3"
          >
            <div
              v-if="trainingProtocol.selected"
              class="mb-3 text-body-1 text--darken-2"
            >
              {{ trainingProtocol.selected.description }}
            </div>

            <div
              v-for="item in trainingProtocolItems"
              :key="item.id"
              class="mb-1"
            >
              <superset-selected-card
                v-if="item.type === 'superset'"
                dense
                :orderable="false"
                :editable="false"
                :item.sync="item"
              />
              <group-selected-card
                v-else-if="item.type === 'group'"
                dense
                :orderable="false"
                :editable="false"
                :item.sync="item"
              />
              <pause-selected-card
                v-else-if="item.type === 'pause'"
                dense
                :orderable="false"
                :editable="false"
                :item.sync="item"
              />
              <exercise-selected-card-template
                v-else-if="item.group.group_id"
                dense
                :orderable="false"
                :editable="false"
                :exercise="item"
              />
            </div>
          </div>
        </div>

        <v-switch
          v-if="trainingProtocol.selected"
          v-model="eventForm.player_can_view_training_protocol"
          class="pa-0 ma-0 mb-6"
          hide-details
          dense
          :label="$t('switch.training_protocol.players_allowed')"
        />
      </div>

      <v-switch
        v-if="eventForm.event_type === eventTypeTestSession || eventForm.event_type === eventTypeReadiness"
        v-model="eventForm.is_player_diagnostic_entry_allowed"
        class="pa-0 ma-0 mb-6"
        hide-details
        dense
        :label="$t('player_enter_diagnostic_results.label')"
      />

      <div v-if="startsProgram">
        <v-row
          no-gutters
          class="mb-3"
          align="center"
        >
          <v-col>
            <subtitle>
              <v-icon
                left
                small
              >
                fal fa-user
              </v-icon>
              {{ $t("eventDetails.reservations.coaches") }}
            </subtitle>
          </v-col>
          <v-col
            cols="auto"
            class="pl-6"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  class="text-none"
                  text
                  small
                  v-on="on"
                  @click="$emit('apply-all', 'coach_ids', eventForm.coach_ids)"
                >
                  {{ $t('training_programs.dialog.event_form.apply_all') }}
                </v-btn>
              </template>
              <span>
                {{ $t('training_programs.dialog.event_form.applies_coaches') }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>

        <validation-provider
          v-slot="{ errors }"
          :rules="{ required: !!eventForm.is_open_application && !startsProgram }"
        >
          <v-autocomplete
            v-model="eventForm.coach_ids"
            outlined
            :items="coaches.data"
            :loading="coaches.loading"
            item-value="id"
            :item-text="(coach) => coach.first_name + ' ' + coach.last_name"
            dense
            multiple
            clearable
            :error-messages="errors"
            :placeholder="$t('eventDetails.reservations.coaches.placeholder')"
          />
        </validation-provider>
      </div>

      <v-row
        no-gutters
        class="mb-3"
        align="center"
      >
        <v-col>
          <subtitle>
            <v-icon
              left
              small
            >
              fal fa-location-dot
            </v-icon>
            {{ $t('dropdown.location.title') }}
          </subtitle>
        </v-col>
        <v-col
          cols="auto"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                class="text-none"
                text
                small
                v-on="on"
                @click="$emit('apply-all', 'location_id', eventForm.location_id)"
              >
                {{ $t('training_programs.dialog.event_form.apply_all') }}
              </v-btn>
            </template>
            <span>
              {{ $t('training_programs.dialog.event_form.applies_locations') }}
            </span>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-autocomplete
        v-model="eventForm.location_id"
        outlined
        :items="location.items"
        item-value="id"
        item-text="name"
        dense
        :placeholder="$t('dropdown.location.placeholder')"
      />

      <v-row class="mb-0">
        <v-col>
          <subtitle>
            <v-icon
              left
              small
            >
              fal fa-list
            </v-icon>
            {{ $t('individual_analysis.notes') }}
          </subtitle>
        </v-col>
        <v-col
          cols="auto"
          class="pl-4"
        >
          <v-switch
            v-model="eventForm.is_description_players_visible"
            class="pa-0 ma-0"
            hide-details
            dense
            :label="$t('switch.visible_to_players')"
          />
        </v-col>
      </v-row>

      <div>
        <rich-text-editor v-model="eventForm.description" />
      </div>

      <v-divider class="mb-6" />

      <v-overlay
        color="white"
        light
        :value="fileUpload.isDragging"
        :opacity="0.8"
        @dragleave.prevent="dragleave"
        @dragover.prevent="dragover"
        @drop.prevent="drop"
      >
        <div
          class="mb-4 text-center"
          style="pointer-events: none"
        >
          <v-icon
            x-large
            color="grey darken-4"
          >
            fal fa-upload
          </v-icon>
        </div>
        <div
          class="grey--text text--darken-4"
          style="pointer-events: none"
        >
          {{ $t("drag_n_drop.instruction") }}
        </div>
      </v-overlay>

      <v-row
        align="center"
        class="mb-4"
      >
        <v-col class="text-body-2 grey--text">
          {{ $t("media_upload.instruction") }}
        </v-col>
        <v-col cols="auto">
          <v-btn
            color="grey darken-2"
            text
            class="text-none"
            @click="$refs.uploadFile.click()"
          >
            {{ $t('button.browse') }}
            <v-icon
              right
              small
            >
              fal fa-upload
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <input
        id="upload-file"
        ref="uploadFile"
        type="file"
        style="display: none"
        accept="video/*,audio/*,image/*,.doc,.docx,.pptx,.xlsx,.xls,application/pdf"
        @input="handleFileChange"
      >

      <div>
        <v-row>
          <v-col
            v-for="(mediaItem, mediaItemIndex) in eventForm.media"
            :key="mediaItem.id"
            cols="6"
          >
            <media-grid-item
              :media-item="mediaItem"
              class="media-grid-item"
              @preview="preview"
              @delete="mediaItem.id ? startMediaDelete(mediaItem) : eventForm.media.splice(mediaItemIndex, 1)"
            />
          </v-col>
        </v-row>
      </div>
    </validation-observer>

    <training-protocol-form-dialog
      :visible.sync="trainingProtocolDialog"
      :edit-items="trainingProtocolItems"
      :listen-events="false"
      :selectable-players="selectedPlayersForProtocol"
      :edit-title="trainingProtocol.selected ? trainingProtocol.selected.name : undefined"
      :edit-description="trainingProtocol.selected ? trainingProtocol.selected.description : undefined"
      @update:edit-items="updateTrainingProtocolItems"
    />

    <media-viewer-dialog
      :visible.sync="mediaViewerDialog.visible"
      :media="eventForm.media"
      :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
      @media:updated="eventForm.media.splice(eventForm.media.findIndex(m => m.id === $event.id), 1, $event)"
    />

    <delete-confirmation-dialog
      :visible.sync="deleteMedia.dialog"
      :submitting="deleteMedia.deleting"
      @confirmed="destroyMedia()"
    />
  </div>
</template>

<script>
import Subtitle from '@/components/helpers/Subtitle.vue'
import RichTextEditor from '@/components/inputs/RichTextEditor.vue'
import SupersetSelectedCard from '@/components/training-builder/SupersetSelectedCard.vue'
import ExerciseSelectedCardTemplate from '@/components/training-builder/ExerciseSelectedCardTemplate.vue'
import GroupSelectedCard from '@/components/training-builder/GroupSelectedCard.vue'
import PauseSelectedCard from '@/components/training-builder/PauseSelectedCard.vue'
import TrainingProtocolFormDialog from '@/components/dialogs/TrainingProtocolFormDialog.vue'
import dayjs from 'dayjs'
import { EventType, EventTypes } from '@/data/models'
import { cloneDeep, difference, intersection, union } from 'lodash'
import timezones from 'timezones-list'
import { CoachApiService, DiagnosticProtocolApiService, LocationApiService, PlayerApiService } from '@/data/services'
import { TrainingProtocolApiService } from '@/data/services/TrainingProtocolApiService'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import _axios from '@/plugins/axios'
import { MediaApiService } from '@/data/services/MediaApiService'
import { EventBus } from '@/plugins/bus'
import { getDateTimeFormatForUser } from '@/utils/helpers'

export default {
  name: 'TrainingProgramEventForm',
  components: {
    MediaViewerDialog,
    DeleteConfirmationDialog,
    MediaGridItem,
    TrainingProtocolFormDialog,
    PauseSelectedCard,
    GroupSelectedCard,
    ExerciseSelectedCardTemplate,
    SupersetSelectedCard,
    RichTextEditor,
    Subtitle
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    startsProgram: {
      type: Boolean,
      default: true
    },
    startDate: {
      type: String,
      default: null
    },
    eventDate: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      eventTypeSelector: EventType.TRAINING,
      datePickerMenuVisible: false,
      submitting: false,
      players: {
        data: [],
        loading: false
      },
      coaches: {
        data: [],
        loading: false
      },
      location: {
        items: [],
        itemsLoading: false
      },
      protocol: {
        items: [],
        itemsLoading: false
      },
      trainingProtocol: {
        items: [],
        itemsLoading: false,
        selected: null
      },
      trainingProtocolItems: [],
      trainingProtocolDialog: false,
      fileUpload: {
        isDragging: false,
        isUploading: false
      },
      mediaViewerDialog: {
        visible: false,
        media: undefined
      },
      deleteMedia: {
        media: undefined,
        dialog: false,
        deleting: false
      }
    }
  },
  computed: {
    eventForm: {
      get: function () {
        return this.event
      },
      set: function (value) {
        this.$emit('update:event', value)
      }
    },
    teamSelectItems() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasTimezoneManagement() {
      return this.user.organisation_settings.has_timezone_management
    },
    hasTrainingBuilder() {
      return this.user.organisation_settings.has_training_builder_module
    },
    hasOpenEventApplications() {
      return this.user.organisation_settings.has_open_event_applications_module
    },
    playersToSelect() {
      return this.players.data
    },
    selectedPlayersForProtocol() {
      return this.players.data.filter(p => this.eventForm.player_ids.includes(p.id))
    },
    otherEventTypes() {
      return Object.keys(EventTypes).map(k => {
        return {
          ...EventTypes[k],
          key: k,
          disabled: this.eventForm.id && k === EventType.TEST_SESSION && this.editingRules && !this.editingRules.canEditToTestSession
        }
      }).filter(t => t.is_other === true)
    },
    openApplicationStartAt() {
      if (!this.eventForm.date || !this.eventForm.start_time) return undefined

      const daysBefore = parseFloat(this.eventForm.open_application_start_days_before)
      const hoursBefore = parseFloat(this.eventForm.open_application_start_hours_before)

      if (isNaN(daysBefore) && isNaN(hoursBefore)) return undefined

      let date = dayjs(this.eventDate + ' ' + dayjs(this.eventForm.start_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'))

      if (!isNaN(daysBefore)) date = date.subtract(daysBefore, 'day')
      if (!isNaN(hoursBefore)) date = date.subtract(hoursBefore, 'hour')

      return date.toDate()
    },
    openApplicationEndAt() {
      if (!this.eventForm.date || !this.eventForm.start_time) return undefined

      const daysBefore = parseFloat(this.eventForm.open_application_end_days_before)
      const hoursBefore = parseFloat(this.eventForm.open_application_end_hours_before)

      if (isNaN(daysBefore) && isNaN(hoursBefore)) return undefined

      let date = dayjs(this.eventDate + ' ' + dayjs(this.eventForm.start_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'))

      if (!isNaN(daysBefore)) date = date.subtract(daysBefore, 'day')
      if (!isNaN(hoursBefore)) date = date.subtract(hoursBefore, 'hour')

      return date.toDate()
    },
    editingRules() {
      if (!this.eventForm.editing_rules) return undefined

      const editingRules = this.eventForm.editing_rules

      return {
        canEditTime: editingRules?.can_edit_time ?? true,
        canEditTeamsAndPlayers: editingRules?.can_edit_teams_and_players ?? true,
        onlyOnePlayer: editingRules?.only_one_player ?? false,
        cannotAddAdditionalPlayers: editingRules?.cannot_add_additional_players ?? false,
        canDelete: editingRules?.can_delete ?? true,
        canEditType: editingRules?.can_edit_type ?? true,
        canEditToTraining: editingRules?.can_edit_to_training ?? true,
        canEditToMatch: editingRules?.can_edit_to_match ?? true,
        canEditToOther: editingRules?.can_edit_to_other ?? true,
        canEditToTestSession: editingRules?.can_edit_to_test_session ?? true
      }
    }
  },
  watch: {
    'event.internal_id': function () {
      this.loadEvent()
    },
    'eventForm.player_ids': function (playerIds) {
      this.cleanExerciseGroupPlayers(playerIds)
    },
    'eventForm.team_ids': function (oldValue, newValue) {
      const addedTeamIds = difference(oldValue, newValue)
      const removedTeamIds = difference(newValue, oldValue)

      const removePlayerIds = this.playersToSelect.filter(p => removedTeamIds.indexOf(p.team_id) > -1).map(p => p.id)
      const addPlayerIds = this.playersToSelect.filter(p => addedTeamIds.indexOf(p.team_id) > -1).map(p => p.id)

      this.eventForm.player_ids = union(difference(this.eventForm.player_ids, removePlayerIds), addPlayerIds)

      const removeCoachIds = this.coaches.data.filter(c => intersection(c.teams.map(t => t.id), removedTeamIds).length > 0).map(c => c.id)
      const addCoachIds = this.coaches.data.filter(c => intersection(c.teams.map(t => t.id), addedTeamIds).length > 0).map(c => c.id)

      this.eventForm.coach_ids = union(difference(this.eventForm.coach_ids, removeCoachIds), addCoachIds)
    }
  },
  created() {
    this.fetchAllProtocols()
    this.fetchAllTrainingProtocols()
    this.fetchAllLocations()
    this.fetchAllCoaches()
    this.fetchAllPlayers()

    const items = []

    let startTime = dayjs().startOf('day')
    const day = startTime.day()

    while (startTime.day() === day) {
      items.push(startTime.format(getDateTimeFormatForUser('HH:mm')))
      startTime = startTime.add(15, 'm')
    }

    this.timeItems = items

    this.timezones = timezones

    this.eventTypeMatch = EventType.MATCH
    this.eventTypeTraining = EventType.TRAINING
    this.eventTypeTestSession = EventType.TEST_SESSION
    this.eventTypeReadiness = EventType.READINESS
    this.eventTypeRehab = EventType.REHAB
    this.eventTypeIndividualWork = EventType.INDIVIDUAL_WORK

    EventBus.$on(this.$events.TRAINING_PROTOCOL_CREATED, () => {
      this.fetchAllTrainingProtocols()
    })

    this.loadEvent()
  },
  methods: {
    async loadEvent() {
      if (this.eventForm.event_type === EventType.TRAINING || this.eventForm.event_type === EventType.MATCH) {
        this.eventTypeSelector = this.eventForm.event_type
      } else {
        this.eventTypeSelector = 'other'
      }

      if (this.eventForm.training_protocol_id) {
        this.trainingProtocol.selected = this.trainingProtocol.items.find(tp => tp.id === this.eventForm.training_protocol_id)

        if (this.eventForm.training_exercises && this.eventForm.training_exercises.length > 0) {
          this.trainingProtocolItems = cloneDeep(this.eventForm.training_exercises)
        } else {
          this.changeTrainingProtocol(this.trainingProtocol.selected)
        }
      } else {
        this.trainingProtocol.selected = null
        this.trainingProtocolItems = []
      }
    },
    async fetchAllProtocols() {
      try {
        this.protocol.itemsLoading = true

        const result = await DiagnosticProtocolApiService.getAll({})

        this.protocol.items = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_protocols.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.protocol.itemsLoading = true
      }
    },
    async fetchAllLocations() {
      try {
        this.location.itemsLoading = true

        const result = await LocationApiService.getAll()

        this.location.items = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_locations.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.location.itemsLoading = true
      }
    },
    async fetchAllCoaches() {
      try {
        this.coaches.loading = true

        const result = await CoachApiService.getPaginated({})

        this.coaches.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.coach_get.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.coaches.loading = false
      }
    },
    async fetchAllPlayers() {
      try {
        this.players.loading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC' })

        this.players.data = result.data
      } finally {
        this.players.loading = false
      }
    },
    updateEventTime() {
      const startAt = dayjs.tz(this.eventForm.date + ' ' + dayjs(this.eventForm.start_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'), this.eventForm.tz || undefined)
      let endAt = dayjs.tz(this.eventForm.date + ' ' + dayjs(this.eventForm.end_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'), this.eventForm.tz || undefined)

      if (!startAt.isValid() || !endAt.isValid()) return

      if (endAt.isBefore(startAt)) {
        this.eventForm.end_time = startAt.add(1, 'hour').format('HH:mm')

        endAt = dayjs.tz(this.eventForm.date + ' ' + dayjs(this.eventForm.end_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'), this.eventForm.tz || undefined)
      }

      this.eventForm.start_at = startAt.toISOString()
      this.eventForm.start = startAt.toDate()
      this.eventForm.end_at = endAt.toISOString()
      this.eventForm.end = endAt.toDate()
    },
    async fetchAllTrainingProtocols() {
      try {
        this.trainingProtocol.itemsLoading = true

        const result = await TrainingProtocolApiService.getAll({})

        this.trainingProtocol.items = result.data

        if (this.trainingProtocol.selected && !this.trainingProtocol.items.some(tp => tp.id === this.trainingProtocol.selected.id)) {
          this.trainingProtocol.items.push(cloneDeep(this.trainingProtocol.selected))
        }

       if (this.eventForm.training_protocol_id && !this.trainingProtocol.selected) {
         this.trainingProtocol.selected = this.trainingProtocol.items.find(tp => tp.id === this.eventForm.training_protocol_id)
       }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.training_protocols_get.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.trainingProtocol.itemsLoading = false
      }
    },
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      this.eventForm.is_dirty = false
      this.eventForm.is_validated = true

      this.$emit('update:event', this.eventForm)
    },
    changeTrainingProtocol(protocol) {
      if (!protocol) {
        this.trainingProtocolItems = []
        this.eventForm.training_protocol_id = null
        this.eventForm.training_exercises = []

        return
      }

      this.trainingProtocolItems = cloneDeep(protocol.exercises)

      this.eventForm.training_protocol_id = protocol.id
      this.eventForm.training_exercises = cloneDeep(protocol.exercises)
    },
    updateTrainingProtocolItems(items) {
      this.trainingProtocolItems = cloneDeep(items)
      this.eventForm.training_exercises = cloneDeep(items)
    },
    cleanExerciseGroupPlayers(validPlayerIds) {
      const cleanGroups = (groups) => {
        if (!groups || !Array.isArray(groups)) return
        groups.forEach(group => {
          if (group.player_ids) group.player_ids = group.player_ids.filter(id => validPlayerIds.includes(id))
          if (group.inside_joker_ids) group.inside_joker_ids = group.inside_joker_ids.filter(id => validPlayerIds.includes(id))
          if (group.outside_joker_ids) group.outside_joker_ids = group.outside_joker_ids.filter(id => validPlayerIds.includes(id))
        })
      }

      const cleanExercises = (exercises) => {
        if (!exercises) return
        exercises.forEach(exercise => {
          if (exercise.params?.SETS) {
            exercise.params.SETS.forEach(set => cleanGroups(set.groups))
          }
          if (exercise.exercises) cleanExercises(exercise.exercises)
        })
      }

      cleanExercises(this.trainingProtocolItems)
    },
    async handleFileChange() {
      await this.$nextTick()

      const files = [...this.$refs.uploadFile.files]

      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'dkg', 'csv']

      for (const file of files) {
        const type = file.type
        const extension = file.name.split('.').pop()

        if (type.match('image.*') || type.match('video.*') || type.match('audio.*') || allowedExtensions.indexOf(extension) > -1) {
          this.eventForm.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            upload: true
          })
        } else {
          this.eventForm.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            error: 'File type not allowed'
          })
        }
      }

      this.$nextTick(() => {
        this.$vuetify.goTo('.media-grid-item', { container: '.v-dialog.v-dialog--active .v-card__text' })
      })

      this.processUpload()
    },
    async processUpload() {
      let formData

      for (const file of this.eventForm.media.filter(m => !!m.upload)) {
        formData = new FormData()
        formData.append('file', file.file, file.name)

        _axios.post('media/upload', formData, {
          onUploadProgress: (e) => {
            file.progress = Number((e.loaded / e.total) * 100)
          }
        }).then(response => {
          file.upload = false

          Object.assign(file, response.data.media)
        }).catch(error => {
          if (error.response && error.response.status === 422) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              color: 'error',
              text: this.$t('alert.file_type_not_allowed.error')
            })

            file.error = this.$t('alert.file_type_not_allowed.error')

            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('event_details.media_upload.error')
          })

          file.error = this.$t('event_details.media_upload.error')
        })
      }
    },
    async destroyMedia(event) {
      if (!this.deleteMedia.media) return

      const id = this.deleteMedia.media.id

      try {
        this.deleteMedia.deleting = true

        await MediaApiService.delete(id)

        const index = this.eventForm.media.findIndex(m => m.id === id)

        if (index > -1) this.eventForm.media.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.deleteMedia.dialog = false

        EventBus.$emit(this.$events.TRAINING_EXERCISE_UPDATED, this.trainingExerciseFormData)
      } catch (e) {

      } finally {
        this.deleteMedia.media = undefined
        this.deleteMedia.deleting = false
      }
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    startMediaDelete(media) {
      this.deleteMedia.media = media
      this.deleteMedia.dialog = true
    }
  }

}
</script>
